import { LowerCasePipe, NgTemplateOutlet, PercentPipe } from '@angular/common';
import { Component, computed, input, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EAnalyticsStatus, EMatchingType } from '@enums/campaign-analytics.enum';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  imports: [
    IconComponent,
    NgTemplateOutlet,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    LowerCasePipe,
    PercentPipe,
    NgxSkeletonLoaderModule,
  ],
  selector: 'app-analytics-status-badge',
  templateUrl: './analytics-status-badge.component.html',
})
export class AnalyticsStatusBadgeComponent {
  public readonly hideStatusBadge = input<boolean>(false);
  public readonly currentCount = input<number | null>(null);
  public readonly currentRatio = input<number | null>(null);
  public readonly executionStatus = input<EMatchingType | null>(null);
  public readonly loading = input<boolean>(false);
  public readonly neutralCompletedStatus = input<boolean>(false);
  public readonly jumpActionDisabled = input<boolean>(false);
  public readonly showJumpAction = input<boolean>(false);
  public readonly showJumpActionLoader = input<boolean>(false);
  public readonly showPillLoader = input<boolean>(false);
  public readonly status = input.required<EAnalyticsStatus | null>();
  public readonly statusBackgroundColor = input<string | null>(null);

  public readonly jumpAction = output<void>();

  public readonly statusIcon = computed(() => {
    const currentStatus = this.status();

    switch (currentStatus) {
      case EAnalyticsStatus.ok:
        return '/icons/circle-check.svg';
      case EAnalyticsStatus.failed:
        return '/icons/circle-x.svg';
      case EAnalyticsStatus.problem:
        return '/icons/warning.svg';
      case EAnalyticsStatus.processing:
        return '/icons/circle-dashed.svg';
      default:
        return '/icons/circle-dashed.svg';
    }
  });

  public readonly statusColor = computed(() => {
    const currentStatus = this.status();

    switch (currentStatus) {
      case EAnalyticsStatus.ok:
        if (this.neutralCompletedStatus()) {
          return 'var(--shikenso-info-base)';
        }

        return 'var(--shikenso-success-base)';
      case EAnalyticsStatus.failed:
        return 'var(--shikenso-error-bright)';
      case EAnalyticsStatus.problem:
        return 'var(--shikenso-warning-base)';
      case EAnalyticsStatus.processing:
        return 'var(--shikenso-info-base)';
      default:
        return 'var(--shikenso-info-base)';
    }
  });

  public readonly pillBackgroundColor = computed(() => {
    const currentStatus = this.status();

    switch (currentStatus) {
      case EAnalyticsStatus.ok:
        if (this.neutralCompletedStatus()) {
          return 'var(--shikenso-info-light)';
        }

        return 'var(--shikenso-success-light)';
      case EAnalyticsStatus.failed:
        return 'var(--shikenso-error-light)';
      case EAnalyticsStatus.problem:
        return 'var(--shikenso-warning-light)';
      case EAnalyticsStatus.processing:
        return 'var(--shikenso-gray-base)';
      default:
        return 'var(--shikenso-gray-base)';
    }
  });

  public readonly EMatchingType = EMatchingType;

  public readonly jumpActionVisible = computed(() => {
    return this.showJumpAction();
  });

  public readonly jumpActionDisabledVisible = computed(() => {
    return this.jumpActionDisabled();
  });

  public readonly loadingVisible = computed(() => {
    return this.loading();
  });

  public readonly jumpActionLoaderVisible = computed(() => {
    return this.showJumpActionLoader();
  });

  public readonly pillLoaderVisible = computed(() => {
    return this.showPillLoader();
  });

  public onJumpActionClick(event: Event): void {
    event.stopPropagation();

    if (this.jumpActionDisabled()) {
      return;
    }

    this.jumpAction.emit();
  }
}
